/**
 * The Decisions surface on the queue grammar: which group a decision sits in
 * (by what the reader owes it), how its row reads, and which two verbs its
 * sheet footer carries. Pure, so the grouping and the verb table are tested
 * without a network.
 */
import type { CodeDecision } from "@yagni/shared";
import { kindLabel, standingOf, type Standing } from "./decisionCopy.js";

export type DecisionGroupKey = "needs" | "relied" | "history";

export interface DecisionGroups {
  /** Active and unconfirmed: a person still has to stand behind it or retire it. */
  needs: CodeDecision[];
  /** Active and confirmed, the most cited first. */
  relied: CodeDecision[];
  /** Superseded, then discarded. */
  history: CodeDecision[];
}

export function groupOf(decision: Pick<CodeDecision, "status" | "verification">): DecisionGroupKey {
  const standing = standingOf(decision);
  if (standing === "assumption") return "needs";
  if (standing === "confirmed") return "relied";
  return "history";
}

export function groupDecisions(items: readonly CodeDecision[]): DecisionGroups {
  const groups: DecisionGroups = { needs: [], relied: [], history: [] };
  const seen = new Set<string>();
  for (const decision of items) {
    if (seen.has(decision.id)) continue;
    seen.add(decision.id);
    groups[groupOf(decision)].push(decision);
  }
  groups.relied.sort((a, b) => b.citedCount - a.citedCount);
  groups.history.sort((a, b) => historyRank(a) - historyRank(b));
  return groups;
}

function historyRank(decision: Pick<CodeDecision, "status">): number {
  return decision.status === "superseded" ? 0 : 1;
}

/** The standing dot: blue and breathing while it waits on a person. */
export function standingDot(standing: Standing): { color: string; mode?: "wait" } {
  switch (standing) {
    case "assumption":
      return { color: "var(--accent)", mode: "wait" };
    case "confirmed":
      return { color: "var(--accent-active)" };
    default:
      return { color: "var(--text-quiet)" };
  }
}

/** The kind as the row's tag: "Architecture", "Product", "Convention". */
export function kindTag(kind: string | null | undefined): string | null {
  const label = kindLabel(kind);
  if (!label) return null;
  return label.replace(/ call$/, "").replace(/^Team /, "");
}

/** The row's state line: the question it settles, and how often agents cited it. */
export function decisionStateLine(decision: Pick<CodeDecision, "question" | "citedCount">): string {
  return decision.citedCount > 0 ? `${decision.question} · cited ×${decision.citedCount}` : decision.question;
}

export function rowVerb(decision: Pick<CodeDecision, "status" | "verification">): "Review" | "Open" {
  return groupOf(decision) === "needs" ? "Review" : "Open";
}

export type DecisionVerb = "confirm" | "discard" | "unconfirm" | "supersede" | "reactivate";

/**
 * The footer by standing: unconfirmed is Discard / Confirm, confirmed is
 * Unconfirm / Supersede, retired is Reactivate alone.
 */
export function footerVerbs(standing: Standing): { secondary: DecisionVerb | null; primary: DecisionVerb } {
  switch (standing) {
    case "assumption":
      return { secondary: "discard", primary: "confirm" };
    case "confirmed":
      return { secondary: "unconfirm", primary: "supersede" };
    default:
      return { secondary: null, primary: "reactivate" };
  }
}

export const VERB_LABEL: Record<DecisionVerb, string> = {
  confirm: "Confirm",
  discard: "Discard",
  unconfirm: "Unconfirm",
  supersede: "Supersede",
  reactivate: "Reactivate",
};
